import { X } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

/**
 * The slice's first overlay: the INV-05 history panel and the INV-06 manual
 * adjustment / correction dialogs. Deliberately small — a labelled
 * role="dialog" over a backdrop, Escape and the backdrop both close it — so
 * the screens can be built without pulling a dialog primitive in ahead of the
 * design system deciding on one.
 */
export function Modal({
  title,
  onClose,
  children,
  className,
  /**
   * Whether the BACKDROP and Escape close it. Default true, because most
   * overlays are things you look at.
   *
   * PART 9 has two that are not, and both say so outright. §8.5's activate
   * prompt: "Clicking the dark background does nothing. Unlike the other two
   * modals, this one cannot be dismissed by clicking outside — you must choose
   * one of the two buttons. That is deliberate, and it must be built that
   * way." And MFG-04 §5.5's voucher form: "Clicking the dark background does
   * not close this form. That is deliberate: it is a data-entry form and it
   * must be hard to lose by accident."
   *
   * The × button stays either way — a modal with no exit is a trap, and
   * neither section asks for one.
   */
  dismissible = true,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  className?: string | undefined;
  dismissible?: boolean;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!dismissible) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, dismissible]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8">
      {dismissible ? (
        <button
          type="button"
          aria-label={t('common.cancel')}
          tabIndex={-1}
          className="absolute inset-0 cursor-default"
          onClick={onClose}
        />
      ) : null}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          'relative z-10 w-full max-w-2xl rounded-xl border bg-background p-5 shadow-lg',
          className,
        )}
      >
        <div className="mb-4 flex items-center gap-3">
          <h3 className="me-auto text-base font-bold">{title}</h3>
          <button
            type="button"
            // GEN-182/NFR-37: an icon-only control needs BOTH. `aria-label`
            // names it for a screen reader; `title` is the only thing a
            // sighted pointer user gets, and an unlabelled × next to a
            // heading is guessable rather than known. They carry the same
            // sentence on purpose.
            aria-label={t('common.close')}
            title={t('common.close')}
            onClick={onClose}
            className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
